import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  LogOut,
  Receipt,
  Wallet,
  Users,
  Pencil,
  Check,
  X,
  Menu,
  HelpCircle,
} from 'lucide-react';
import type { UserDto } from '@bstm/shared';
import { api, ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { toast } from 'sonner';
import Tutorial from './Tutorial';

const navItem = ({ isActive }: { isActive: boolean }) =>
  `flex items-center gap-2 px-3 py-2 rounded-md transition-colors ${
    isActive ? 'bg-zinc-800 text-white' : 'text-zinc-400 hover:text-white hover:bg-zinc-900'
  }`;

function NameEditor({ user }: { user: UserDto }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(user.name ?? '');

  const save = useMutation({
    mutationFn: (name: string) =>
      api<{ user: UserDto }>('/me', { method: 'PATCH', json: { name } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me'] });
      setEditing(false);
      toast.success('Name updated');
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Failed'),
  });

  function commit() {
    const trimmed = draft.trim();
    if (trimmed.length < 1) {
      toast.error('Name cannot be empty');
      return;
    }
    save.mutate(trimmed);
  }

  if (!editing) {
    return (
      <button
        onClick={() => {
          setDraft(user.name ?? '');
          setEditing(true);
        }}
        className="group flex items-center gap-1.5 text-xs text-zinc-300 hover:text-white"
        title="Edit your name"
      >
        <span className="truncate max-w-[160px]">{user.name}</span>
        <Pencil size={11} className="text-zinc-600 group-hover:text-zinc-400" />
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <input
        autoFocus
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') setEditing(false);
        }}
        maxLength={80}
        className="text-xs py-0.5 px-1.5 w-[140px]"
      />
      <button onClick={commit} className="text-emerald-400 hover:text-emerald-300" title="Save">
        <Check size={12} />
      </button>
      <button
        onClick={() => setEditing(false)}
        className="text-zinc-500 hover:text-zinc-300"
        title="Cancel"
      >
        <X size={12} />
      </button>
    </div>
  );
}

function NavLinks({
  isAdmin,
  onNavigate,
  onOpenTutorial,
}: {
  isAdmin: boolean;
  onNavigate?: () => void;
  onOpenTutorial: () => void;
}) {
  return (
    <>
      <NavLink to="/" end className={navItem} onClick={onNavigate}>
        <Receipt size={16} /> Tickets
      </NavLink>
      <NavLink to="/positions" className={navItem} onClick={onNavigate}>
        <Wallet size={16} /> Positions
      </NavLink>
      {isAdmin && (
        <NavLink to="/admin/users" className={navItem} onClick={onNavigate}>
          <Users size={16} /> Admin
        </NavLink>
      )}
      <button
        type="button"
        onClick={() => {
          onNavigate?.();
          onOpenTutorial();
        }}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-zinc-400 hover:text-white hover:bg-zinc-900"
      >
        <HelpCircle size={16} /> Tutorial
      </button>
    </>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [tutorialOpen, setTutorialOpen] = useState(false);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  if (!user) return null;
  const isAdmin = user.role === 'ADMIN';

  return (
    <div className="min-h-full md:flex">
      {/* Mobile top bar */}
      <header className="md:hidden flex items-center gap-3 px-4 py-3 border-b border-zinc-800 bg-zinc-950 sticky top-0 z-30">
        <button
          onClick={() => setDrawerOpen(true)}
          className="p-1.5 -ml-1.5 rounded-md hover:bg-zinc-900 text-zinc-300"
          aria-label="Open menu"
        >
          <Menu size={20} />
        </button>
        <Link to="/" className="text-sm font-semibold flex-1 truncate">Ticket Manager</Link>
        <NameEditor user={user} />
      </header>

      {/* Mobile drawer backdrop */}
      {drawerOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/60 z-40"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      {/* Sidebar (always visible md+, drawer on mobile) */}
      <aside
        className={`fixed md:static inset-y-0 left-0 z-50 w-64 border-r border-zinc-800 bg-zinc-950 flex flex-col transform transition-transform md:translate-x-0 ${
          drawerOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="p-4 border-b border-zinc-800 flex items-start justify-between">
          <div>
            <Link to="/" className="text-base font-semibold">Ticket Manager</Link>
            <div className="mt-1">
              <NameEditor user={user} />
            </div>
            <div className="text-xs text-zinc-500 font-mono mt-0.5">{user.phone}</div>
          </div>
          <button
            onClick={() => setDrawerOpen(false)}
            className="md:hidden p-1 -mr-1 -mt-1 text-zinc-400 hover:text-white"
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        </div>
        <nav className="p-3 space-y-1 flex-1">
          <NavLinks
            isAdmin={isAdmin}
            onNavigate={() => setDrawerOpen(false)}
            onOpenTutorial={() => setTutorialOpen(true)}
          />
        </nav>
        <div className="p-3 border-t border-zinc-800">
          <button onClick={logout} className="btn-secondary w-full justify-center">
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>

      <Tutorial active={tutorialOpen} onExit={() => setTutorialOpen(false)} />
    </div>
  );
}
